import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Lock } from "lucide-react";
import { ModalBackdrop } from "./ModalBackdrop";
import { useEscapeKey } from "./useEscapeKey";
import { type ShelfBook, shelfSlug } from "../lib/shelves";
import { saveVideoPosition, progressNote } from "../lib/video-progress";
import { useShelfOverflow } from "../lib/shelf-overflow";
import { ownsVideoProduct, videoState } from "../lib/video-access";
import { t } from "../strings.js";
import { APP_MODE } from "../lib/appMode";

// ROADMAP ITEM 7 — **THE VIDEO SHELF COMES OUT OF `App.tsx`, OPPORTUNISTICALLY, BECAUSE THIS IS THE
// AREA THIS WINDOW TOUCHED.** (2026-09-04)
//
// Her standing law: *"refactor `src/App.tsx` into components OPPORTUNISTICALLY as areas are touched
// — no big-bang rewrite."* This window answered `bixler_cody`'s ticket out of the video library and
// had to read `handleVideoStream` to know whether an Apprentice could watch tonight, so the video
// surface is the area that was touched.
//
// **THREE COMPONENTS AND NOTHING ELSE MOVED.** The player, the card and the shelf: they call each
// other and nothing else in `App.tsx` calls into them except `LibraryPage`, which now imports them.
// Their bodies are UNCHANGED, comments and all — an extraction that also edits is two changes
// wearing one diff, and the second one is the one nobody reviews.
//
// **EVERYTHING THEY NEED WAS ALREADY SHARED**: `ModalBackdrop` is its own component (R81),
// `ShelfBook` is `src/lib/shelves.ts`'s type, and the icons and `t` are imports `App.tsx` already
// held. Nothing had to be widened to let them leave, which is what made this the cheap one to take.

export // The in-site video player. Streams the class video from our own gated endpoint (the
// member never leaves the site, never sees the Dropbox URL), resumes from the saved
// position, and saves the position back as they watch so My Room can pick it up.

function VideoPlayerModal({ slug, title, startAt = 0, onClose, language }: {
  slug: string;
  title: string;
  language: string;
  startAt?: number;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSaved = useRef(0);
  // R55: the sentence that replaces the promise below the player when the house is NOT keeping her
  // place. Empty while saves are landing, because a success is not worth a word.
  const [saveNote, setSaveNote] = useState("");

  const saveProgress = (final: boolean) => {
    const el = videoRef.current;
    if (!el || !Number.isFinite(el.currentTime)) return;
    const positionSeconds = el.currentTime;
    // **THE THROTTLE STAYS ATTEMPT-BASED AND THAT IS A RULING** (the whole argument is in
    // `src/lib/video-progress.ts`): stamping only on success would open this gate on every
    // `timeupdate` during an outage, which is a request every 250ms. A lost intermediate ping costs
    // nothing — the next one records a LATER position — so only an all-refused session is a real
    // loss, and that is exactly what the sentence covers.
    if (!final && Math.abs(positionSeconds - lastSaved.current) < 10) return;
    lastSaved.current = positionSeconds;
    const durationSeconds = Number.isFinite(el.duration) ? el.duration : undefined;
    // R55: the answer is READ now. It used to be drained to text and discarded, so all four of this
    // route's refusals passed under a line promising her place was being kept.
    saveVideoPosition({ slug, positionSeconds, durationSeconds })
      .then((answer) => setSaveNote(progressNote(answer, { signIn: t(language, "videos.signIn"), failed: t(language, "video.progressFailed") })));
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onLoaded = () => {
      if (startAt > 5 && startAt < (el.duration || Infinity) - 5) el.currentTime = startAt;
    };
    const onTime = () => saveProgress(false);
    const onPause = () => saveProgress(true);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("pause", onPause);
    return () => {
      saveProgress(true);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("pause", onPause);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEscapeKey(onClose);

  return (
    <ModalBackdrop label={t(language, "video.playerLabel", { title })} className="video-backdrop">
      <div className="video-modal">
        <button className="modal-close" type="button" onClick={onClose} aria-label={t(language, "video.closeVideo")}>x</button>
        <p className="eyebrow">{t(language, "library.tab.video")}</p>
        <h2 className="video-modal__title">{title}</h2>
        <video
          ref={videoRef}
          className="video-modal__player"
          src={`/api/video/stream/${encodeURIComponent(slug)}`}
          controls
          autoPlay
          playsInline
          preload="metadata"
        />
        {/* R55: the promise and its contradiction are ONE line, never both at once — a sentence
            saying her place is kept, sitting above one saying it is not, is worse than either. */}
        <p className={`video-modal__note${saveNote ? " video-modal__note--warn" : ""}`}>
          {saveNote || t(language, "video.progressNote")}
        </p>
      </div>
    </ModalBackdrop>
  );
}

// A face-out video cover card. Uses a real cover image when one has been supplied
// (served like a book cover), otherwise a designed gold placeholder bearing the title,
// with a resume bar when the member has already started it.
// T8-9 — THE SHELF SHOWS EVERY TIER WHAT IS THERE. The owner watched the video shelf VANISH below
// Vet, so she could not see what the membership was even for. The card now carries its own state:
// a PRICE when the video is a one-time product she has not bought, a LOCK when playback needs a
// tier she is not on, and the plain play face when it is hers. The card is never removed and never
// disabled — tapping a locked one is how she meets the purple box, which is the whole point of
// browsing being free.
export function VideoCard({ book, progress, onOpen, canPlay, product, owned, language }: {
  book: ShelfBook;
  language: string;
  progress?: { positionSeconds: number; durationSeconds: number | null };
  onOpen: (book: ShelfBook) => void;
  canPlay: boolean;
  product?: { key: string; label: string; priceText: string } | null;
  owned?: boolean;
}) {
  const [imgOk, setImgOk] = useState(true);
  const pct = progress && progress.durationSeconds
    ? Math.min(100, Math.round((progress.positionSeconds / progress.durationSeconds) * 100))
    : 0;
  // T1-7/T8-9's three faces, in ONE law (`src/lib/video-access.ts`, which has the whole finding).
  const state = videoState(product, Boolean(owned), canPlay);
  const needsPurchase = state === "buy";
  const locked = state !== "play";
  return (
    <button
      type="button"
      className={locked ? "video-card video-card--locked" : "video-card"}
      onClick={() => onOpen(book)}
      title={book.title}
      aria-label={t(language, locked ? "video.locked" : "video.play", { title: book.title })}
    >
      <span className="video-card__cover">
        {imgOk ? (
          <img className="video-card__img" src={`/api/library/cover/${book.slug}`} alt="" loading="lazy" onError={() => setImgOk(false)} />
        ) : (
          <span className="video-card__placeholder">{book.title}</span>
        )}
        {locked ? (
          <span className="video-card__lock" aria-hidden="true"><Lock size={18} strokeWidth={1.6} /></span>
        ) : (
          <span className="video-card__play" aria-hidden="true">▶</span>
        )}
        {/* F1 (her box, 2026-10-17): in the store app a paid video wears no price; the popup says it is not in her plan. */}
        {needsPurchase && product && !APP_MODE ? (
          <span className="video-card__price">{product.priceText}</span>
        ) : null}
        {pct > 0 ? (
          <span className="video-card__bar"><span className="video-card__bar-fill" style={{ width: `${pct}%` }} /></span>
        ) : null}
      </span>
      <span className="video-card__title">{book.title}</span>
    </button>
  );
}

// One category's video shelf: the same floating gold-chrome shelf shell as the books,
// but the videos stand FACE-OUT (cover forward) instead of spine-out.
export function VideoShelf({ category, books, progress, onOpen, canPlay, productFor, ownedKeys, language }: {
  category: string;
  language: string;
  books: ShelfBook[];
  progress: Record<string, { positionSeconds: number; durationSeconds: number | null }>;
  onOpen: (book: ShelfBook) => void;
  // T8-9: the shelf renders at EVERY tier, so each card is told what this member may do with it.
  canPlay: boolean;
  productFor: (slug: string) => { key: string; label: string; priceText: string } | null;
  ownedKeys: readonly string[];
}) {
  const { booksRef, overflow, slide } = useShelfOverflow(books.length, 280);
  return (
    <section id={shelfSlug(category)} className="shelf-row shelf-row--video" aria-label={t(language, "library.shelf.videos", { name: category })}>
      <div className="shelf-row__label">
        <span>{category}</span>
      </div>
      <div className="shelf-row__unit">
        <div className="shelf-row__scroller">
          <button type="button" className={`shelf-arrow shelf-arrow--left${overflow.left ? "" : " shelf-arrow--off"}`} onClick={() => slide(-1)} aria-label={t(language, "library.shelf.slideVideosLeft", { name: category })} tabIndex={overflow.left ? 0 : -1}>
            <ArrowLeft size={18} />
          </button>
          <div className="shelf-row__books shelf-row__books--video" ref={booksRef}>
            {books.map((book) => {
              const product = productFor(book.slug);
              return (
                <VideoCard
                  key={book.slug}
                  book={book}
                  language={language}
                  progress={progress[book.slug]}
                  onOpen={onOpen}
                  canPlay={canPlay}
                  product={product}
                  owned={ownsVideoProduct(product, ownedKeys)}
                />
              );
            })}
          </div>
          <button type="button" className={`shelf-arrow shelf-arrow--right${overflow.right ? "" : " shelf-arrow--off"}`} onClick={() => slide(1)} aria-label={t(language, "library.shelf.slideVideosRight", { name: category })} tabIndex={overflow.right ? 0 : -1}>
            <ArrowRight size={18} />
          </button>
        </div>
        <div className="shelf-plank" aria-hidden="true">
          <span className="shelf-plank__finial shelf-plank__finial--l">❖</span>
          <span className="shelf-plank__finial shelf-plank__finial--r">❖</span>
        </div>
      </div>
    </section>
  );
}
